import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { columns } from '../internal/data/gridData';
import { useSelector } from 'react-redux';
import { getApiDateRangeFromFilterState } from '../../helpers/transactionMetrics';
import { rulesEngineGet } from '../../services/rulesEngineClient';
import { getRulesEngineTransactionsUrl } from '../../services/rulesEngineEndpoints';

function normalizeStatus(value) {
  if (!value)
    return 'Aguardando Análise';

  const raw = String(value).trim().toUpperCase();

  if (raw === 'FINALIZED' || raw === 'FINALIZADO' || raw === 'FINALIZADA')
    return 'Finalizado';

  if (raw === 'REPORT' || raw === 'REPORTED' || raw === 'REPORTADO' || raw === 'REPORTADA')
    return 'Reportado';

  if (raw === 'WAITING_ANALYSIS' || raw === 'AGUARDANDO_ANALISE')
    return 'Aguardando Análise';

  if (raw === 'ANALYSIS' || raw === 'ANALISE' || raw === 'EM_ANALISE')
    return 'Em Análise';

  if (raw.includes('FINAL'))
    return 'Finalizado';

  if (raw.includes('REPORT'))
    return 'Reportado';

  if (raw.includes('WAITING') || raw.includes('AGUARD'))
    return 'Aguardando Análise';

  if (raw.includes('ANALIS'))
    return 'Em Análise';

  return 'Aguardando Análise';
}

function extractTransactionStatus(item) {
  const candidates = [
    item?.analysis_status,
    item?.status,
    item?.operation_status,
    item?.transaction_status,
    item?.analysisStatus,
  ];

  for (const candidate of candidates) {
    if (!candidate)
      continue;

    if (typeof candidate === 'string')
      return normalizeStatus(candidate);

    if (typeof candidate === 'object') {
      const nestedValue = candidate.id || candidate.code || candidate.value || candidate.name || candidate.label;
      if (nestedValue)
        return normalizeStatus(nestedValue);
    }
  }

  return 'Aguardando Análise';
}

export default function CustomizedDataGrid({ analysisStatus = 'ALL' }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [rowCount, setRowCount] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 20,
  });
  const filterState = useSelector((state) => state.filter);
  const dateRange = React.useMemo(() => getApiDateRangeFromFilterState(filterState), [filterState]);
  const cacheRef = React.useRef({});

  React.useEffect(() => {
    cacheRef.current = {};
  }, [analysisStatus, dateRange.endDate, dateRange.startDate]);

  React.useEffect(() => {
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
  }, [analysisStatus]);

  React.useEffect(() => {
    const fetchTransactions = async () => {
      const cacheKey = `${paginationModel.page}_${paginationModel.pageSize}_${dateRange.startDate}_${dateRange.endDate}_${analysisStatus}`;

      if (cacheRef.current[cacheKey]) {
        setLoading(false);
        setRows(cacheRef.current[cacheKey].rows);
        setRowCount(cacheRef.current[cacheKey].rowCount);
        return;
      }

      try {
        setLoading(true);
        const responseData = await rulesEngineGet(getRulesEngineTransactionsUrl(process.env.REACT_APP_API_RULES_URL), {
          params: {
            page: String(paginationModel.page + 1),
            per_page: String(paginationModel.pageSize),
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            has_alerts: 'true',
            analysis_status: analysisStatus,
          }
        });

        const transactions = responseData.transactions || responseData;
        const meta = responseData.meta;

        if (meta) {
          setRowCount(meta.total_count || 0);
        }

        if (!Array.isArray(transactions)) {
          console.error('Dados não são um array:', transactions);
          setRows([]);
          return;
        }

        const formattedData = transactions.map(item => ({
          id: item.operation_id,
          status: extractTransactionStatus(item),
          value: `R$ ${item.value}`,
          transactionType: item.transaction_type,
          eventCount: item.alerts_summary?.total_alerts || 0,
          dateHourTransaction: new Date(item.transaction_datetime).toLocaleString('pt-BR'),
          appointments: item.alerts_summary?.alerts?.map(alert => alert.description) || []
        }));

        cacheRef.current[cacheKey] = {
          rows: formattedData,
          rowCount: meta?.total_count || 0
        };

        setRows(formattedData);
      } catch (error) {
        console.error('Erro ao buscar transações:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [analysisStatus, dateRange.endDate, dateRange.startDate, paginationModel.page, paginationModel.pageSize]);

  return (
    <DataGrid
      loading={loading}
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[10, 20, 50]}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      disableColumnResize
      density="compact"
      slotProps={{
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: {
              variant: 'outlined',
              size: 'small',
            },
            columnInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            operatorInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined',
                size: 'small',
              },
            },
          },
        },
      }}
    />
  );
}
